/**
 * Allowlisted Merge Agent Handler connector slugs.
 * Keep in sync with ml-scribe
 * ``apis/v2/integrations/merge_agent_handler/connectors.py``.
 *
 * AH catalog uses underscores for multi-word slugs (``google_calendar``),
 * not Nango-style hyphens (``google-calendar``).
 *
 * Auth:
 * - ``none`` — public APIs; always available, no Merge Link
 * - ``secrets`` / ``oauth`` — clinician completes Merge Link
 */

export type MergeConnectorAuthMode = 'none' | 'secrets' | 'oauth';

export type MergeConnectorCategory =
  | 'workspace'
  | 'clinical'
  | 'collaboration'
  | 'wearable'
  | 'esign';

export const MERGE_ALLOWED_CONNECTORS = [
  // Workspace (OAuth)
  'gmail',
  'outlook',
  'google_calendar',
  'google_drive',
  'google_docs',
  'google_sheets',
  'google_meet',
  'microsoft_teams',
  'onenote',
  'dropbox',
  'box',
  'onedrive',
  'sharepoint',
  // Collaboration
  'slack',
  'notion',
  'zoom',
  // E-sign
  'docu_sign',
  // Clinical / public (no auth)
  'clinicaltrials',
  'cms_coverage',
  'npi_registry',
  'biorxiv',
  // Clinical / secrets
  'adobe_pdf_services',
  'pubmed',
  // Wearables (patient links their own account)
  'oura',
  'whoop',
] as const;

export type MergeConnectorSlug = (typeof MERGE_ALLOWED_CONNECTORS)[number];

const AUTH_MODE: Record<MergeConnectorSlug, MergeConnectorAuthMode> = {
  gmail: 'oauth',
  outlook: 'oauth',
  google_calendar: 'oauth',
  google_drive: 'oauth',
  google_docs: 'oauth',
  google_sheets: 'oauth',
  google_meet: 'oauth',
  microsoft_teams: 'oauth',
  onenote: 'oauth',
  dropbox: 'oauth',
  box: 'oauth',
  onedrive: 'oauth',
  sharepoint: 'oauth',
  slack: 'oauth',
  notion: 'oauth',
  zoom: 'oauth',
  docu_sign: 'oauth',
  clinicaltrials: 'none',
  cms_coverage: 'none',
  npi_registry: 'none',
  biorxiv: 'none',
  adobe_pdf_services: 'secrets',
  pubmed: 'secrets',
  oura: 'oauth',
  whoop: 'oauth',
};

const CATEGORY: Record<MergeConnectorSlug, MergeConnectorCategory> = {
  gmail: 'workspace',
  outlook: 'workspace',
  google_calendar: 'workspace',
  google_drive: 'workspace',
  google_docs: 'workspace',
  google_sheets: 'workspace',
  google_meet: 'workspace',
  microsoft_teams: 'workspace',
  onenote: 'workspace',
  dropbox: 'workspace',
  box: 'workspace',
  onedrive: 'workspace',
  sharepoint: 'workspace',
  slack: 'collaboration',
  notion: 'collaboration',
  zoom: 'collaboration',
  docu_sign: 'esign',
  clinicaltrials: 'clinical',
  cms_coverage: 'clinical',
  npi_registry: 'clinical',
  biorxiv: 'clinical',
  adobe_pdf_services: 'clinical',
  pubmed: 'clinical',
  oura: 'wearable',
  whoop: 'wearable',
};

const LABELS: Record<string, string> = {
  gmail: 'Gmail',
  outlook: 'Outlook',
  google_calendar: 'Google Calendar',
  google_drive: 'Google Drive',
  google_docs: 'Google Docs',
  google_sheets: 'Google Sheets',
  google_meet: 'Google Meet',
  microsoft_teams: 'Microsoft Teams',
  onenote: 'OneNote',
  dropbox: 'Dropbox',
  box: 'Box',
  onedrive: 'OneDrive',
  sharepoint: 'SharePoint',
  slack: 'Slack',
  notion: 'Notion',
  zoom: 'Zoom',
  docu_sign: 'DocuSign',
  clinicaltrials: 'ClinicalTrials.gov',
  cms_coverage: 'CMS Coverage',
  npi_registry: 'NPI Registry',
  biorxiv: 'bioRxiv',
  adobe_pdf_services: 'Adobe PDF Services',
  pubmed: 'PubMed',
  oura: 'Oura',
  whoop: 'WHOOP',
};

const NO_DEDICATED_TOOLS = 'Connect available. No dedicated Heidi tools.';

/**
 * Short “what Heidi can do” copy for the connectors list.
 *
 * This is a promise the agent has to keep, so the copy describes Heidi's
 * registered tools rather than the app's own features. Keep in step with
 * `TOOLS` below.
 *
 * “No dedicated tools” is not the same as unreachable: the agent's Merge
 * passthrough (`integrations.merge.read` / `.write` / `.delete`) can drive
 * anything the Tool Pack exposes for a connected app. What it cannot promise is
 * a specific capability per connector, which is what this copy is for.
 */
const CAPABILITIES: Record<MergeConnectorSlug, string> = {
  gmail: 'Search and read email — subject, sender, date, and preview',
  outlook: 'Search Outlook mail and list calendar events',
  google_calendar: 'List upcoming calendar events',
  google_drive: 'Browse and open Drive files',
  google_docs: 'Read a document, or draft a new one',
  google_sheets: 'Read a cell range, or append a row',
  google_meet: 'List conferences and read meeting transcripts',
  microsoft_teams: 'List teams and channels, and post channel messages',
  onenote: 'List notebooks and sections, and create pages',
  dropbox: 'Browse and open Dropbox files',
  box: 'Browse and open Box files',
  onedrive: 'Browse and open OneDrive files',
  sharepoint: 'Browse and open SharePoint files',
  slack: NO_DEDICATED_TOOLS,
  notion: 'Search the workspace, read pages, and create new ones',
  zoom: 'List meetings and read meeting transcripts',
  docu_sign: NO_DEDICATED_TOOLS,
  clinicaltrials: 'Search ClinicalTrials.gov studies by condition or term',
  cms_coverage: 'Look up Medicare LCDs and local coverage articles',
  npi_registry: 'Look up US providers by NPI number or name',
  biorxiv: 'List recent bioRxiv and medRxiv preprints',
  adobe_pdf_services: NO_DEDICATED_TOOLS,
  pubmed: 'Search PubMed and read article summaries',
  oura: 'Read daily sleep and readiness scores',
  whoop: 'Read daily sleep and recovery scores',
};

export type MergeConnectorToolAccess = 'read' | 'write';

export interface MergeConnectorTool {
  /** Human-readable description of what the tool does */
  description: string;
  access: MergeConnectorToolAccess;
  /** Writes that require clinician approval in Work */
  requiresApproval?: boolean;
}

const t = (
  description: string,
  access: MergeConnectorToolAccess,
  requiresApproval?: boolean
): MergeConnectorTool => ({
  description,
  access,
  ...(requiresApproval ? { requiresApproval: true } : {}),
});

/**
 * Tool-level “what Heidi can do” for the connector detail sheet.
 *
 * One entry per **registered** Merge Agent Handler tool in ml-scribe
 * (`service/tools/registrations/merge_agent_handler.py`). An empty list means
 * Connect works but the agent has no tool yet, and the sheet says so — listing
 * a tool here that isn't registered makes the agent refuse work the UI
 * promised.
 */
const TOOLS: Record<MergeConnectorSlug, MergeConnectorTool[]> = {
  gmail: [
    t('Search inbox for referrals, patient names, and threads', 'read'),
    t('Read email subject, sender, date, and preview', 'read'),
  ],
  outlook: [
    t('Search Outlook mail for referrals and patient threads', 'read'),
    t('List upcoming Outlook calendar events', 'read'),
  ],
  google_calendar: [t('List upcoming Google Calendar events', 'read')],
  google_drive: [
    t('Browse and open Drive files', 'read'),
    t('Save letters and summaries to Drive', 'write', true),
  ],
  google_docs: [
    t('Read a document as plain text', 'read'),
    t('Create a document from a draft', 'write', true),
  ],
  google_sheets: [
    t('Read a cell range in A1 notation', 'read'),
    t('Append a row to a sheet', 'write', true),
  ],
  google_meet: [
    t('List recent Meet conferences', 'read'),
    t('Read a conference transcript', 'read'),
  ],
  microsoft_teams: [
    t('List Microsoft Teams you belong to', 'read'),
    t("List a team's channels", 'read'),
    t('Post a channel message', 'write', true),
  ],
  onenote: [
    t('List notebooks and sections', 'read'),
    t('Create OneNote pages in a notebook section', 'write', true),
  ],
  dropbox: [
    t('Browse and open Dropbox files', 'read'),
    t('Save letters and summaries to Dropbox', 'write', true),
  ],
  box: [
    t('Browse and open Box files', 'read'),
    t('Save letters and summaries to Box', 'write', true),
  ],
  onedrive: [
    t('Browse and open OneDrive files', 'read'),
    t('Save letters and summaries to OneDrive', 'write', true),
  ],
  // Search only — Agent Handler has no SharePoint upload tool wired.
  sharepoint: [t('Browse and open SharePoint files', 'read')],
  // Slack, Box and DocuSign are absent from the Agent Handler connector
  // catalogue, so Link cannot succeed for them — Nango covers all three.
  slack: [],
  notion: [
    t('Search pages and databases', 'read'),
    t('Read a page and its content blocks', 'read'),
    t('Create a page under an existing one', 'write', true),
  ],
  zoom: [
    t('List recent and upcoming meetings', 'read'),
    t('Read a meeting transcript', 'read'),
  ],
  docu_sign: [],
  clinicaltrials: [t('Search ClinicalTrials.gov studies by condition or term', 'read')],
  cms_coverage: [t('Look up Medicare LCDs and local coverage articles', 'read')],
  npi_registry: [t('Look up US providers by NPI number or name', 'read')],
  biorxiv: [t('List recent bioRxiv and medRxiv preprints', 'read')],
  adobe_pdf_services: [],
  pubmed: [t('Search PubMed and read article summaries', 'read')],
  oura: [t('Read daily sleep and readiness scores', 'read')],
  whoop: [t('Read daily sleep and recovery scores', 'read')],
};

/** Connectors that need Merge Link (OAuth or API-key paste). */
export const MERGE_LINKABLE_CONNECTORS = MERGE_ALLOWED_CONNECTORS.filter(
  (slug) => AUTH_MODE[slug] !== 'none'
);

export function getMergeConnectorLabel(slug: string): string {
  return LABELS[slug] ?? slug;
}

export function getMergeConnectorCapability(slug: string): string {
  return (
    CAPABILITIES[slug as MergeConnectorSlug] ??
    'Connected app tools via Merge Agent Handler'
  );
}

export function getMergeConnectorTools(slug: string): MergeConnectorTool[] {
  return TOOLS[slug as MergeConnectorSlug] ?? [];
}

export function getMergeConnectorAuthMode(slug: string): MergeConnectorAuthMode {
  return AUTH_MODE[slug as MergeConnectorSlug] ?? 'oauth';
}

export function getMergeConnectorCategory(
  slug: string
): MergeConnectorCategory | undefined {
  return CATEGORY[slug as MergeConnectorSlug];
}

export function mergeConnectorRequiresLink(slug: string): boolean {
  return getMergeConnectorAuthMode(slug) !== 'none';
}

export function isMergeConnectorConnected(
  slug: string,
  connections: ReadonlyArray<{ connector: string; always_available?: boolean }>
): boolean {
  if (!mergeConnectorRequiresLink(slug)) {
    return true;
  }
  return connections.some(
    (connection) => connection.connector === slug && !connection.always_available
  );
}
